import { useEffect, useMemo, useState } from 'react';
import {
  Loader2, AlertCircle, Clock, CheckCircle2,
} from 'lucide-react';
import {
  SALES_PARTNER_EXISTING_ADMIN_ROLE_MESSAGE, SALES_PARTNER_INACTIVE_ACCOUNT_MESSAGE, type SalesPartnerInviteAccount,
} from '@evi/shared';
import {
  validateSalesPartnerInvite, acceptSalesPartnerInvite, requestSalesPartnerInviteSignInLink,
  getDisplayErrorMessage,
  type InviteSignIn, type InviteSignedIn, type SalesPartnerInviteValidateResponse,
} from '@/lib/api';
import { agentPayoutCountryDisplay } from '@/lib/agentPayoutCountry';
import { useAuthStore } from '@/store/auth';
import { FIRST_JOB_PATH, isMfaPending, setMfaFlow } from '@/lib/mfaFlow';
import {
  INVITE_SIGN_IN_COPY, SALES_PARTNER_ROLE_LABEL, existingAccountLine, otherAccountLine, readInvitePageUrl, takeInviteReturn,
} from '@/lib/inviteSignIn';
import { InviteSignInPanel } from '@/components/ui/InviteSignInPanel';
import styles from './SalesPartnerInvitePage.module.css';

/**
 * Phase 7g.3 — Sales Partner invite landing page.
 *
 * Public surface (no auth required to view). Rendered by App.tsx
 * when the URL path is /sales-partner-invite, regardless of the
 * current auth state — an authenticated user hitting their own
 * already-consumed invite hits the 'accepted' branch and gets a
 * "Go to login" link instead of the password form.
 *
 * Flow:
 *   1. Mount: parse `token` from URL query, call validate endpoint
 *   2. Render one of six states:
 *      - loading       : spinner while validate is in flight
 *      - invalid       : "this link is invalid"
 *      - expired       : "this link has expired"
 *      - accepted      : "already used" + go-to-login button
 *      - valid         : welcome card with SP identity + Set up my account
 *      - submitting    : the same card while the account is set up
 *   3. On success: the authenticator setup (/auth/mfa/enroll) with the pending token; that
 *      screen signs the partner in (Warren, 21 October 2026: no session before the second factor).
 *
 * Passwordless since 20 October 2026 (Warren): one press sets up the account,
 * then the authenticator. An email that already has an account (29 October
 * 2026, Warren: "accepting it asks them to sign in and adds the new role to
 * that account, never 'email taken'") reads "You already have an EV
 * Initiative account with this email. Sign in to add Sales Partner access to
 * it." and signs in as that account here (password, a sign-in link to its
 * mailbox, or Google, components/ui/InviteSignInPanel.tsx); the partner role
 * is added then, and its authenticator step follows (set up, or its
 * challenge). Signed in as someone else: refused, with what to do. A login
 * that already holds an admin role, or is inactive, sees the refusal up front
 * and has no button.
 *
 * Design mirrors LoginPage's card + halo + form treatment — same
 * dark canvas, same accent-green CTA, same input styling — so the
 * SP feels like they're entering the same product their inviter
 * was using.
 *
 * Phase 7g.4 will ship the actual SP product surface (nav-filtered
 * dashboard, scope-restricted backend routes); until then, the SP
 * lands on the admin '/' route and sees mostly-403s. Warren-internal
 * testing only — the rolodex copy-invite-link UI is the rollout
 * control until 7g.4 ships.
 */

type Phase =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | { kind: 'expired' }
  | { kind: 'accepted' }
  | { kind: 'valid'; agent: ValidatedAgent }
  | { kind: 'submitting'; agent: ValidatedAgent };

interface ValidatedAgent {
  name: string;
  email: string;
  /** The payout account's country, only when one already exists. Null
   *  for a new Sales Partner: the country is chosen when they set up
   *  payouts, so there is nothing true to show yet. */
  country: string | null;
  expiresAt: string;
  /** What accepting does for this email (from validate). */
  account: SalesPartnerInviteAccount;
  /** Who this browser is signed in as, if anyone (from validate). */
  signedIn: InviteSignedIn;
}

interface Props {
  /** Caller-supplied navigation callback. Used after successful
   *  acceptance to land the SP on '/' under their fresh auth
   *  state. Optional so legacy direct-render call sites (none
   *  today) don't break — missing → falls back to a full reload
   *  via window.location.assign(). */
  onAccepted?: (to: string) => void;
}

export function SalesPartnerInvitePage({ onAccepted }: Props) {
  const setAuth = useAuthStore((s) => s.setAuth);

  // Parse once from the URL: the invite token (or the one kept for Google's
  // return) and a one-time sign-in token from the emailed sign-in link or
  // Google (?sign_in=), which is stripped from the address bar at once. An
  // absent token short-circuits to the 'invalid' branch.
  const arrival = useMemo(() => {
    const url = readInvitePageUrl(window.location.search);
    if (url.signIn) {
      try { window.history.replaceState(null, '', `${window.location.pathname}${url.rest ? `?${url.rest}` : ''}`); } catch { /* keep going */ }
    }
    return { token: url.token || takeInviteReturn('sales_partner') || '', signIn: url.signIn };
  }, []);
  const token = arrival.token;

  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [submitError, setSubmitError] = useState('');

  // Validate on mount. Empty token bypasses the network call —
  // there's nothing to validate.
  useEffect(() => {
    if (!token) {
      setPhase({ kind: 'invalid' });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await validateSalesPartnerInvite(token);
        if (cancelled) return;
        if (res.valid && res.agent_name && res.agent_email && res.expires_at) {
          setPhase({
            kind: 'valid',
            agent: {
              name: res.agent_name,
              email: res.agent_email,
              country: res.agent_country ?? null,
              expiresAt: res.expires_at,
              account: res.account ?? 'new',
              signedIn: res.signed_in ?? null,
            },
          });
        } else {
          // Map the server's three rejection reasons to the
          // matching phase. Unknown reason (defensive) lands on
          // 'invalid' — the most conservative copy.
          const reason: SalesPartnerInviteValidateResponse['reason'] =
            res.reason ?? 'invalid';
          if (reason === 'accepted') setPhase({ kind: 'accepted' });
          else if (reason === 'expired') setPhase({ kind: 'expired' });
          else setPhase({ kind: 'invalid' });
        }
      } catch (err) {
        if (cancelled) return;
        // Network failure — treat as invalid; the alternative is a
        // distinct "couldn't reach server" surface that drivers
        // wouldn't know how to action either. Console-debug for
        // any diagnostic follow-up.
        // eslint-disable-next-line no-console
        console.warn('validateSalesPartnerInvite failed', err);
        setPhase({ kind: 'invalid' });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Passwordless (Warren, 20 October 2026): one press sets up a new partner. An existing account accepts with its own
  // sign-in (signIn: its password or a one-time sign-in link), or with the session it is already signed in with.
  const accept = async (agent: ValidatedAgent, signIn?: InviteSignIn): Promise<void> => {
    setSubmitError('');
    setPhase({ kind: 'submitting', agent });
    try {
      const res = await acceptSalesPartnerInvite({ token, ...(signIn ? { sign_in: signIn } : {}) });
      // The authenticator comes first (Warren, 21 October 2026): the server answers the MFA pending shape, never a
      // session: set up for a new authenticator, its challenge for an account that has one.
      if (isMfaPending(res)) {
        // Then the terms on one screen, then straight to New job (Warren, 21 October 2026): its form is the "Start a
        // new job" the partner home leads with, one tap closer. The home holds the team, the profile and payouts.
        setMfaFlow(res, { next: FIRST_JOB_PATH });
        const to = res.variant === 'challenge' ? '/auth/mfa/challenge' : '/auth/mfa/enroll';
        if (onAccepted) onAccepted(to);
        else window.location.assign(to);
        return;
      }
      setAuth(res.user, res.access_token);
      if (onAccepted) onAccepted('/');
      else window.location.assign('/');
    } catch (err) {
      setSubmitError(await getDisplayErrorMessage(err, 'Your account could not be set up. Try again, or write to partners@evinitiative.com.'));
      setPhase({ kind: 'valid', agent });
    }
  };
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (phase.kind !== 'valid') return;
    await accept(phase.agent);
  };

  // Back from the emailed sign-in link or Google (?sign_in=): accept with it once, as soon as the invite is read.
  const [signInUsed, setSignInUsed] = useState(false);
  useEffect(() => {
    if (signInUsed || !arrival.signIn || phase.kind !== 'valid' || phase.agent.account !== 'existing') return;
    setSignInUsed(true);
    void accept(phase.agent, { link: arrival.signIn });
  }, [phase, signInUsed]);

  // ── State variants ──────────────────────────────────────────────

  if (phase.kind === 'loading') {
    return (
      <PageShell>
        <div className={styles.loadingRow}>
          <Loader2 size={18} className={styles.spin} />
          <span>Validating invite…</span>
        </div>
      </PageShell>
    );
  }

  if (phase.kind === 'invalid') {
    return (
      <PageShell>
        <div className={styles.statusCard}>
          <div className={`${styles.statusIcon} ${styles.statusIconError}`}>
            <AlertCircle size={24} />
          </div>
          <h1 className={styles.statusTitle}>Invalid invite</h1>
          <p className={styles.statusBody}>
            This invite link is invalid. Contact your administrator if you
            believe this is an error.
          </p>
        </div>
      </PageShell>
    );
  }

  if (phase.kind === 'expired') {
    return (
      <PageShell>
        <div className={styles.statusCard}>
          <div className={`${styles.statusIcon} ${styles.statusIconWarn}`}>
            <Clock size={24} />
          </div>
          <h1 className={styles.statusTitle}>Invite expired</h1>
          <p className={styles.statusBody}>
            This invite has expired. Contact your administrator to request
            a fresh link.
          </p>
        </div>
      </PageShell>
    );
  }

  if (phase.kind === 'accepted') {
    return (
      <PageShell>
        <div className={styles.statusCard}>
          <div className={`${styles.statusIcon} ${styles.statusIconOk}`}>
            <CheckCircle2 size={24} />
          </div>
          <h1 className={styles.statusTitle}>Invite already used</h1>
          <p className={styles.statusBody}>
            This invite has already been accepted. Sign in to access your
            account.
          </p>
          <a href="/login" className={styles.statusActionLink}>Go to login</a>
        </div>
      </PageShell>
    );
  }

  // 'valid' or 'submitting' — both render the form. submitting just
  // disables the submit button + shows a spinner label.
  const submitting = phase.kind === 'submitting';
  const agent = phase.agent;
  const expiresLabel = new Date(agent.expiresAt).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });

  const refused = agent.account === 'admin_role' ? SALES_PARTNER_EXISTING_ADMIN_ROLE_MESSAGE
    : agent.account === 'inactive' ? SALES_PARTNER_INACTIVE_ACCOUNT_MESSAGE
      : agent.signedIn === 'other' ? otherAccountLine(agent.email) : null;
  const existing = agent.account === 'existing';

  return (
    <PageShell>
      <h1 className={styles.title}>Welcome aboard</h1>
      <p className={styles.subtitle}>
        {existing
          ? existingAccountLine(SALES_PARTNER_ROLE_LABEL)
          : 'Set up your Sales Partner account. It signs you in, with no password.'}
      </p>

      {/* Identity context: read-only confirmation of who this invite is for. */}
      <div className={styles.identityBlock}>
        <div className={styles.identityRow}>
          <span className={styles.identityLabel}>Name</span>
          <span className={styles.identityValue}>{agent.name}</span>
        </div>
        <div className={styles.identityRow}>
          <span className={styles.identityLabel}>Email</span>
          <span className={styles.identityValue}>{agent.email}</span>
        </div>
        {agent.country && (
          <div className={styles.identityRow}>
            <span className={styles.identityLabel}>Payout account country</span>
            <span className={styles.identityValue}>{agentPayoutCountryDisplay(agent.country)}</span>
          </div>
        )}
      </div>

      {refused ? (
        <div className={styles.serverError} role="alert">{refused}</div>
      ) : existing && agent.signedIn === 'invitee' ? (
        <form className={styles.form} onSubmit={(e) => void handleSubmit(e)} noValidate>
          <p className={styles.hint}>{INVITE_SIGN_IN_COPY.signedInAs.replace('{email}', agent.email)}</p>
          {submitError && <div className={styles.serverError} role="alert">{submitError}</div>}
          <button type="submit" className={styles.submitBtn} disabled={submitting}>
            {submitting ? 'Setting up your account' : INVITE_SIGN_IN_COPY.acceptSignedIn}
          </button>
        </form>
      ) : existing ? (
        <InviteSignInPanel
          kind="sales_partner"
          token={token}
          email={agent.email}
          roleLabel={SALES_PARTNER_ROLE_LABEL}
          busy={submitting}
          error={submitError}
          onAccept={(signIn) => accept(agent, signIn)}
          onRequestLink={() => requestSalesPartnerInviteSignInLink(token)}
        />
      ) : (
        <form className={styles.form} onSubmit={(e) => void handleSubmit(e)} noValidate>
          {submitError && <div className={styles.serverError} role="alert">{submitError}</div>}
          <button type="submit" className={styles.submitBtn} disabled={submitting}>
            {submitting ? 'Setting up your account' : 'Set up my account'}
          </button>
          <p className={styles.hint} style={{ textAlign: 'center', marginTop: 4 }}>
            Invite link expires {expiresLabel}.
          </p>
        </form>
      )}
    </PageShell>
  );
}

/**
 * Shared shell — halo + card + logo. All state variants render
 * inside the same chrome so the page doesn't visibly jump between
 * loading/valid/error transitions.
 */
function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className={styles.page}>
      <div className={styles.heroBg} />
      <div className={styles.card}>
        <div className={styles.logoRow}>
          <img
            src="/evi-logo.png"
            alt="EV Initiative"
            className={styles.logo}
          />
        </div>
        {children}
      </div>
    </div>
  );
}
